import React, { createContext, useContext, useState, useEffect } from 'react';

const ContentContext = createContext();

export const useContent = () => useContext(ContentContext);

const API_URL = '/api.php';

const defaultContent = {
    // === GLOBAL SETTINGS ===
    global: {
        siteName: "Servicios Futura",
        tagline: "Tu futuro comienza aquí",
        logo: "/logoMain.png",
        favicon: "/favicon.ico",
        primaryColor: "#1e40af",
        secondaryColor: "#f59e0b",
        phone: "+503 7777-8888",
        email: "contacto@futurabr.com",
        whatsapp: "+50360272418",
        address: "Santa Ana, El Salvador",
        workingHours: "Lun - Vie: 8:00 AM - 6:00 PM, Sáb: 9:00 AM - 2:00 PM"
    },

    // === HOME PAGE ===
    home: {
        heroTitle: "Encuentra Tu Hogar Ideal en El Salvador",
        heroSubtitle: "Las mejores propiedades en Santa Ana y todo El Salvador. Inversión segura, asesoría profesional.",
        heroImage: "https://images.unsplash.com/photo-1600596542815-22b8c153bd30?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80",
        heroCTA: "Explorar Propiedades",
        featuredTitle: "Propiedades Destacadas",
        featuredSubtitle: "Explora nuestra selección exclusiva de inmuebles"
    },

    // === ABOUT PAGE ===
    about: {
        title: "Sobre Nosotros",
        subtitle: "Más de 10 años conectando familias con su hogar ideal",
        mission: "Facilitar el acceso a vivienda digna y segura en El Salvador mediante un servicio profesional, transparente y orientado al cliente.",
        vision: "Ser la inmobiliaria líder en El Salvador, reconocida por nuestra innovación tecnológica y excelencia en servicio al cliente.",
        values: ["Transparencia", "Profesionalismo", "Innovación", "Compromiso con el Cliente"],
        teamTitle: "Nuestro Equipo",
        teamDescription: "Profesionales certificados con años de experiencia en el mercado inmobiliario salvadoreño."
    },

    // === SERVICES PAGE ===
    services: {
        title: "Nuestros Servicios",
        subtitle: "Soluciones integrales para todas tus necesidades inmobiliarias",
        servicesList: [
            {
                id: 1,
                name: "Venta de Propiedades",
                description: "Asesoría completa en la compra de tu propiedad ideal",
                icon: "home"
            },
            {
                id: 2,
                name: "Alquiler",
                description: "Encuentra el lugar perfecto para rentar",
                icon: "key"
            },
            {
                id: 3,
                name: "Avalúos",
                description: "Valuación profesional de tu propiedad",
                icon: "calculator"
            },
            {
                id: 4,
                name: "Asesoría Financiera",
                description: "Te ayudamos a obtener tu crédito hipotecario",
                icon: "dollar"
            }
        ]
    },

    // === CLEANING SERVICE ===
    cleaning: {
        title: "Futura Cleaning",
        subtitle: "Servicio profesional de limpieza de muebles y colchones",
        promoTitle: "✨ Renueva tus Muebles",
        promoText: "Sistema de inyección-extracción. Resultados profesionales.",
        whatsappNumber: "50360272418",
        transportBaseFee: 5,
        transportPricePerKm: 0.50,
        benefits: [
            "Eliminación de ácaros y bacterias",
            "Restauración de colores",
            "Productos biodegradables",
            "Secado rápido"
        ]
    },

    // === FINANCIAL CENTER ===
    financial: {
        title: "Centro Financiero",
        subtitle: "Herramientas para planificar tu inversión",
        calculatorTitle: "Calculadora Hipotecaria",
        calculatorDescription: "Calcula tus cuotas mensuales y total a pagar",
        ratesTitle: "Tasas de Interés",
        ratesDescription: "Compara las mejores opciones del mercado"
    },

    // === CRM ===
    crm: {
        title: "Centro de Operaciones (CRM)",
        subtitle: "Gestiona tu negocio inmobiliario como los profesionales",
        features: [
            "Pipeline de Ventas Visual",
            "Gestión de Contactos",
            "Calendario de Actividades",
            "Dashboard Analítico",
            "Gestor de Tareas"
        ]
    },

    // === MARKETING CENTER ===
    marketing: {
        title: "Centro de Marketing",
        subtitle: "Recursos educativos y herramientas para potenciar tu marca inmobiliaria",
        categories: [
            { id: "image", label: "Imagen", description: "Herramientas de diseño y generación de imágenes" },
            { id: "video", label: "Video", description: "Edición y producción de video" },
            { id: "copy", label: "Copywriting", description: "Redacción de contenido persuasivo" },
            { id: "social", label: "Redes Sociales", description: "Gestión de redes sociales" },
            { id: "ads", label: "Publicidad", description: "Campañas publicitarias efectivas" },
            { id: "landing", label: "Landing Pages", description: "Páginas de aterrizaje optimizadas" }
        ]
    },

    // === SEO SETTINGS ===
    seo: {
        metaTitle: "Servicios Futura | Propiedades en El Salvador",
        metaDescription: "Encuentra tu hogar ideal en El Salvador. Casas, apartamentos y terrenos en venta y alquiler. Asesoría profesional y financiamiento disponible.",
        metaKeywords: "bienes raices, propiedades, casas en venta, apartamentos, terrenos, El Salvador, Santa Ana, inversión inmobiliaria",
        ogImage: "https://images.unsplash.com/photo-1600596542815-22b8c153bd30",
        ogTitle: "Servicios Futura - Tu Hogar Ideal te Espera",
        ogDescription: "Las mejores propiedades en El Salvador. Inversión segura, asesoría profesional.",
        twitterCard: "summary_large_image",
        canonicalUrl: "https://futurabienesraices.com"
    },

    // === ANALYTICS & TRACKING ===
    analytics: {
        googleAnalyticsId: "",
        facebookPixelId: "",
        googleTagManagerId: "",
        hotjarId: "",
        enableTracking: false,
        trackPageViews: true,
        trackConversions: true,
        trackFormSubmissions: true
    },

    // === SOCIAL MEDIA ===
    social: {
        facebook: "https://facebook.com/futurabr",
        instagram: "https://instagram.com/futura_br",
        twitter: "https://twitter.com/futurabr",
        youtube: "https://youtube.com/@futurabr",
        tiktok: "https://tiktok.com/@futurabr",
        linkedin: "https://linkedin.com/company/futurabr",
        showSocialLinks: true
    },

    // === CONTACT FORM ===
    contactForm: {
        enableContactForm: true,
        emailRecipient: "contacto@futurabr.com",
        successMessage: "¡Gracias por contactarnos! Te responderemos pronto.",
        fields: ["name", "email", "phone", "message"],
        requirePhone: true,
        enableCaptcha: false
    },

    // === FOOTER ===
    footer: {
        copyrightText: "© 2026 Servicios Futura. Todos los derechos reservados.",
        showSocialLinks: true,
        showNewsletter: true,
        newsletterTitle: "Suscríbete a nuestro boletín",
        newsletterPlaceholder: "Tu email",
        columns: [
            {
                title: "Empresa",
                links: [
                    { label: "Nosotros", url: "/nosotros" },
                    { label: "Servicios", url: "/servicios" },
                    { label: "Blog", url: "/blog" }
                ]
            },
            {
                title: "Legal",
                links: [
                    { label: "Términos", url: "/terminos" },
                    { label: "Privacidad", url: "/privacidad" }
                ]
            }
        ]
    },

    // === PRO MEMBERSHIP ===
    pro: {
        enabled: true,
        title: "Actualiza a Profesional",
        subtitle: "Herramientas avanzadas de análisis, CRM y Marketing Digital",
        price: 19,
        currency: "USD",
        billingCycle: "mensual",
        features: [
            "CRM Completo",
            "Calculadoras Avanzadas",
            "Marketing Digital",
            "Analíticas Detalladas",
            "Landing Pages Ilimitadas",
            "Soporte Prioritario"
        ]
    }
};

export const ContentProvider = ({ children }) => {
    const [content, setContent] = useState(defaultContent);
    const [isLoading, setIsLoading] = useState(true);

    // Load content from API on mount
    useEffect(() => {
        fetchContent();
    }, []);

    const fetchContent = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_URL}?action=content`);
            if (response.ok) {
                const data = await response.json();
                // Merge fetched data with default content to ensure all keys exist
                setContent(prev => ({
                    ...prev,
                    ...data
                }));
            }
        } catch (error) {
            console.error('Error loading content:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Save specific section to API
    const saveSectionToApi = async (sectionKey, sectionData) => {
        try {
            await fetch(`${API_URL}?action=content`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    section_key: sectionKey,
                    content: sectionData
                })
            });
        } catch (error) {
            console.error(`Error saving section ${sectionKey}:`, error);
        }
    };

    // Update single field
    const updateContent = (section, key, value) => {
        setContent(prev => {
            const newSectionContent = {
                ...prev[section],
                [key]: value
            };

            const newContent = {
                ...prev,
                [section]: newSectionContent
            };

            // Optimistic update
            saveSectionToApi(section, newSectionContent);
            return newContent;
        });
    };

    // Update entire section
    const updateSection = (section, data) => {
        setContent(prev => {
            const newSectionContent = {
                ...prev[section],
                ...data
            };

            const newContent = {
                ...prev,
                [section]: newSectionContent
            };

            saveSectionToApi(section, newSectionContent);
            return newContent;
        });
    };

    // Bulk update multiple sections
    const bulkUpdate = (updates) => {
        setContent(prev => {
            const newContent = { ...prev };
            Object.keys(updates).forEach(section => {
                newContent[section] = {
                    ...newContent[section],
                    ...updates[section]
                };
                saveSectionToApi(section, newContent[section]);
            });
            return newContent;
        });
    };

    // Reset to defaults
    const resetContent = () => {
        setContent(defaultContent);
        // Save all sections to API
        Object.keys(defaultContent).forEach(section => {
            saveSectionToApi(section, defaultContent[section]);
        });
    };

    // Export configuration
    const exportContent = () => {
        const dataStr = JSON.stringify(content, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `futura-config-${new Date().toISOString().split('T')[0]}.json`;
        link.click();
    };

    // Import configuration
    const importContent = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const imported = JSON.parse(e.target.result);
                    setContent(imported);
                    // Save all imported sections
                    Object.keys(imported).forEach(section => {
                        saveSectionToApi(section, imported[section]);
                    });
                    resolve(imported);
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = reject;
            reader.readAsText(file);
        });
    };

    const value = {
        content,
        isLoading,
        updateContent,
        updateSection,
        bulkUpdate,
        resetContent,
        exportContent,
        importContent
    };

    return (
        <ContentContext.Provider value={value}>
            {children}
        </ContentContext.Provider>
    );
};
